import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MessageObject, MessagesData, SendMessageRequest, SendMessageResponse } from './messages.domain';
import { ErrorService } from '../services/error/error.service';
import { LocalStorageService } from '../services/local-storage.service';
import { MessagesService } from './messages.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CommonService } from '../services/common.service';
import { CommunicationService } from '../services/communication.service';
import { TechnicianResponseData } from '../employee-overview/employee-overview.domain';
import { Editor, Quill, TechnicianData } from '../domains/common.domain';
import { EnvironmentService } from '../services/environment.service';
import { EditorStyle, QuillConfig } from '../constants/app.constants';
import { FileService } from '../services/file.service';
import { DocumentResponse } from '../jurisdiction/jurisdiction.domain';

@Component({
  providers: [ConfirmationService, MessagesService],
  selector: 'app-messages',
  styleUrls: ['./messages.component.scss'],
  templateUrl: 'messages.component.html',
})
export class MessagesComponent implements OnInit {
  showTechnicianForm: boolean;
  isLoading: boolean;
  isDescOrder: boolean;
  documents: FormArray;
  messageFG: FormGroup;
  tabsData: MessageObject[];
  tabIndex: number;
  technicianId: string;
  editorStyle: Editor;
  quillConfig: Quill;
  private lastUrl: string;
  private technicianData: TechnicianData;
  private userRole: string;

  constructor(
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private localStorageService: LocalStorageService,
    private messageService: MessageService,
    private messagesService: MessagesService,
    private route: ActivatedRoute,
    private router: Router,
    public environmentService: EnvironmentService,
    private fileService: FileService,
    private formBuilder: FormBuilder
  ) {}

  @HostListener('click', ['$event']) onKeyPress(event) {
    this.fileService.downloadFileFromMessage(event);
  }

  ngOnInit(): void {
    this.userRole = this.localStorageService.getItem('role');
    this.showTechnicianForm = this.userRole === 'employee' || this.userRole === 'administrator';
    this.technicianData = null;
    this.technicianId = this.route.snapshot.params.technicianId || '';
    this.lastUrl = this.route.snapshot.url.length > 1 ? this.route.snapshot.url[0].path : '/';

    this.isDescOrder = true;
    if (this.showTechnicianForm) {
      this.getTechnicianData();
    } else {
      this.tabsData = [];
      this.tabIndex = 0;
      this.getMessages();
    }
    if (this.environmentService.showWordFormat) {
      this.editorStyle = EditorStyle;
      this.quillConfig = QuillConfig;
    }
  }

  convertDateToMilliSeconds(dateValue: string): number {
    const date = new Date(dateValue);
    return date.getTime();
  }

  sortMessagesData(): void {
    this.isDescOrder = !this.isDescOrder;
    if (this.isDescOrder) {
      this.tabsData = this.sortMessages(this.tabsData, 'desc');
    } else {
      this.tabsData = this.sortMessages(this.tabsData);
    }
  }

  getMessages(): void {
    this.isLoading = true;
    this.messagesService.getAllMessages().subscribe(
      (response: MessagesData) => {
        this.errorService.throwErrorIfFailure(response, true);
        if (response.message) {
          this.tabsData = this.sortMessages(response.message, 'desc');
        }
        if (this.tabsData.length) {
          this.updateMessage(0);
        }
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  updateMessage(index: number): void {
    const messageId = this.tabsData[index].message_id;

    if (this.tabsData[index].message_read === '0') {
      this.messagesService.updateMessageReadStatus(Number(messageId)).subscribe(
        (response: MessagesData) => {
          this.tabsData[index] = response.message[0];
          this.tabIndex = index;
          this.cdRef.detectChanges();
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        },
        () => {}
      );
    }
  }

  confirmSendMessage(): void {
    this.confirmationService.confirm({
      accept: () => {
        this.sendMessage();
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  addForm(): void {
    this.documents.push(
      this.formBuilder.group({
        documentId: '',
        documentName: new FormControl(''),
        fileName: '',
      })
    );
  }

  onImageUpload(event, index: number, uploader): void {
    const name = (this.documents.controls[index] as FormGroup).value.documentName;
    if ((this.documents.controls[index] as FormGroup).value.documentId) {
      this.fileService.modifyDocumentForWorkflow(event, (this.documents.controls[index] as FormGroup).value.documentId).subscribe(
        (response: any) => {
          uploader.clear();
          (this.documents.controls[index] as FormGroup).controls.fileName.setValue(
            event.currentFiles && event.currentFiles[0] && event.currentFiles[0].name
          );
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          uploader.clear();
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      const applicationId = this.localStorageService.getItem('application_id');
      this.fileService.uploadDocumentsForMessages(event, applicationId, 'other', name).subscribe(
        (response: DocumentResponse) => {
          (this.documents.controls[index] as FormGroup).controls.fileName.setValue(
            event.currentFiles?.length && event.currentFiles[0].name
          );
          (this.documents.controls[index] as FormGroup).controls.documentId.setValue(response.document[0].document_id);
          uploader.clear();
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          uploader.clear();
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  downloadDocument(documentId: string, fileName: string): void {
    this.fileService.readWorkflowDocument(documentId).subscribe((response: Blob) => {
      this.fileService.downloadDocument(response, fileName);
    });
  }

  removeDocument(id: number): void {
    const documentId = (this.documents.controls[id] as FormGroup).value.documentId;
    if (documentId) {
      this.fileService.removeWorkflowDocument(documentId).subscribe(
        response => {
          this.documents.removeAt(id);
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.documents.removeAt(id);
    }
  }

  private sortMessages(data: MessageObject[], order?: string): MessageObject[] {
    return data.sort((a, b) => {
      if (order === 'desc') {
        return this.convertDateToMilliSeconds(b.message_date) - this.convertDateToMilliSeconds(a.message_date);
      } else {
        return this.convertDateToMilliSeconds(a.message_date) - this.convertDateToMilliSeconds(b.message_date);
      }
    });
  }

  private getTechnicianData(): void {
    if (this.technicianId) {
      this.isLoading = true;
      this.communicationService.getData(`technician?technician_id=${this.technicianId}`).subscribe(
        (response: TechnicianResponseData) => {
          this.errorService.throwErrorIfFailure(response, true);
          this.technicianData = (response.technician && response.technician[0]) || null;
          this.prepareForm();
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        },
        () => {
          this.isLoading = false;
        }
      );
    } else {
      this.prepareForm();
    }
  }

  private prepareForm(): void {
    const dataObj = this.localStorageService.getItem('message_data');
    this.messageFG = new FormGroup({
      message: new FormControl('', Validators.required),
      title: new FormControl(this.technicianId ? dataObj?.subject : '', Validators.required),
      documents: this.formBuilder.array([]),
    });
    this.documents = this.messageFG.get('documents') as FormArray;

    if (this.technicianData) {
      this.messageFG.addControl(
        'firstName',
        new FormControl({
          value: this.technicianData.role === 'customer' ? dataObj?.firstName : this.technicianData.first_name,
          disabled: true,
        })
      );
      this.messageFG.addControl(
        'lastName',
        new FormControl({
          value: this.technicianData.role === 'customer' ? dataObj?.lastName : this.technicianData.last_name,
          disabled: true,
        })
      );
    } else {
      this.messageFG.addControl('userType', new FormControl(false));
      this.messageFG.controls.userType.setValidators(Validators.required);
    }
  }

  private sendMessage(): void {
    let documentLinks = '';
    if (this.documents.controls.length) {
      if (this.documents.value[0].documentId) {
        documentLinks = '<br><label><strong>Anhang:</strong></label>';
      }
      this.documents.controls.forEach(item => {
        if (item.value.documentId) {
          documentLinks = documentLinks.concat(`${
            item.value.documentName.length ? `<br><label>${item.value.documentName}: </label>` : `<br>`
          }<a href="javascript:downloadDocument(\\'${item.value.documentId}\\',\\'${item.value.fileName}\\')"
          id="${item.value.documentId}">${item.value.fileName}</a>`);
        }
      });
    }
    if (this.technicianId) {
      const requestData: SendMessageRequest = {
        message: [
          {
            message_title: this.messageFG.value.title,
            message_text: this.messageFG.value.message,
            technician_id: this.technicianId,
            application_id: this.localStorageService.getItem('application_id'),
          },
        ],
      };
      requestData.message[0].message_text = requestData.message[0].message_text.concat(documentLinks);

      this.messagesService.sendNewMessage(requestData).subscribe(
        (response: SendMessageResponse) => {
          this.errorService.throwErrorIfFailure(response, true);
          this.postSuccessfulSave();
        },
        error => {
          this.errorService.throwErrorIfFailure(error, false);
        }
      );
    } else {
      const requestData: SendMessageRequest = {
        message: [
          {
            message_title: this.messageFG.value.title,
            message_text: this.messageFG.value.message,
          },
        ],
      };
      requestData.message[0].message_text = requestData.message[0].message_text.concat(documentLinks);

      const role = this.messageFG.value.userType === 'customer' ? 'customer' : 'guest_technician,standard_technician';

      this.messagesService.sendMessageToAll(requestData, role).subscribe(
        (response: SendMessageResponse) => {
          this.errorService.throwErrorIfFailure(response, true);
          this.postSuccessfulSave();
        },
        error => {
          this.errorService.throwErrorIfFailure(error, false);
        }
      );
    }
  }

  private postSuccessfulSave(): void {
    const responseMessage = {
      severity: 'success',
      detail: 'messages.success-message',
      life: 3000,
      key: 'app-notifier',
      data: {
        msgType: 'locale',
      },
    };

    if (this.messageService) {
      this.messageService.clear('app-notifier');
      this.messageService.add(responseMessage);
    }
    this.router.navigate([this.lastUrl]);
  }
}
